import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ParticipantStatus, ParticipationMode } from "src/app/core/models/entities/EntityEnum";
import { UserModel } from "src/app/core/models/entities/user.model";
import { Utils } from "src/app/core/utils/utils";

@Component({
    selector: "app-participant-modal",
    templateUrl: "./participant-modal.component.html",
})
export class ParticipantModalComponent {
    @Input() participant?: UserModel;
    @Input() set participationMode(value: ParticipationMode | undefined) {
        if (value) {
            this._participationMode = value;
            this.setParticipationStatues();
        }
    }
    get participationMode(): ParticipationMode | undefined {
        return this._participationMode;
    }
    private _participationMode: ParticipationMode | undefined;
    @Input() set isGroupChief(value: boolean) {
        this._isGroupChief = value;
        this.setParticipationStatues();
    }

    get isGroupChief(): boolean {
        return this._isGroupChief;
    }
    private _isGroupChief: boolean = false;

    @Output() save = new EventEmitter<UserModel>();
    @Output() close = new EventEmitter<void>();

    ParticipantStatus = ParticipantStatus;
    form: FormGroup;
    statuses = Object.values(ParticipantStatus);
    existingDiplomaFile?: string;

    constructor(private fb: FormBuilder) {
        this.form = this.fb.group({
            fullName: ["", Validators.required],
            birthDate: ["", Validators.required],
            city: [""],
            phone: ["", Validators.required],
            email: ["", [Validators.required, Validators.email]],
            status: ["", Validators.required],
            school: ["", Validators.required],
            educationLevel: ["", Validators.required],
            graduateYear: [0],
            matriculeNumber: [""],
            diplomaFile: [null],
        });

        this.handleStatusChanges();
    }

    ngOnInit(): void {
        if (this.participant) {
            this.form.patchValue(this.participant);
            this.existingDiplomaFile = this.participant.diplomaFile?.name;
        }
    }

    setParticipationStatues(): void {
        if (this.isGroupChief || this.participationMode === ParticipationMode.INDIVIDUAL) {
            this.statuses = [
                ParticipantStatus.ARCHI_STUDENT,
                ParticipantStatus.ARCHI_GRADUATE,
            ];
            return;
        }
        this.statuses = [
            ParticipantStatus.ARCHI_STUDENT,
            ParticipantStatus.ARCHI_GRADUATE,
            ParticipantStatus.ENGINEERING_STUDENT,
            ParticipantStatus.URBANISM_STUDENT,
        ];
    }

    handleStatusChanges(): void {
        this.form.get("status")?.valueChanges.subscribe((status) => {
            const diplomaCtrl = this.form.get("diplomaFile");
            const matriculeCtrl = this.form.get("matriculeNumber");

            if (status === ParticipantStatus.ARCHI_GRADUATE) {
                diplomaCtrl?.setValidators([Validators.required]);
                matriculeCtrl?.clearValidators();
                matriculeCtrl?.reset();
            } else {
                matriculeCtrl?.setValidators([Validators.required]);
                diplomaCtrl?.clearValidators();
                diplomaCtrl?.reset();
            }

            diplomaCtrl?.updateValueAndValidity();
            matriculeCtrl?.updateValueAndValidity();
        });
    }

    onFileChange(event: any): void {
        const file = event.target.files[0];
        if (file) {
            this.form.patchValue({ diplomaFile: file });
            this.existingDiplomaFile = undefined;
        }
    }

    isInvalid(controlName: string): boolean {
        const control = this.form.get(controlName);
        return !!(
            control &&
            control.invalid &&
            (control.dirty || control.touched)
        );
    }

    removeDiplomaFile(): void {
        this.existingDiplomaFile = undefined;
        this.form.patchValue({ diplomaFile: null });
    }

    getError(controlName: string): string | null {
        const control = this.form.get(controlName);
        if (!control || !control.errors) return null;

        if (control.errors["required"]) {
            return "Ce champ est obligatoire";
        }

        if (control.errors["email"]) {
            return "Adresse email invalide";
        }

        if (control.errors["min"]) {
            return "Valeur invalide";
        }

        return "Champ invalide";
    }

    submit(): void {
        if (this.form.invalid) return;

        this.save.emit({
            id: this.participant?.id ?? Utils.getNewUid(),
            ...this.form.value,
        });
    }
}
